
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Zap,
  Globe,
  Heart,
  ArrowRight,
  Search,
  Building2,
} from "lucide-react";

import "./About.css";

const stats = [
  { number: "100+", label: "Trainer profiles" },
  { number: "50+", label: "Skill categories" },
  { number: "Pan India", label: "Training reach" },
  { number: "24/7", label: "Opportunity discovery" },
];

const principles = [
  {
    number: "01",
    icon: ShieldCheck,
    title: "Verified quality",
    description:
      "Every trainer profile is checked, so organizations can hire with confidence.",
  },
  {
    number: "02",
    icon: Zap,
    title: "Fast matching",
    description:
      "Skill-based search helps organizations find suitable trainers faster.",
  },
  {
    number: "03",
    icon: Globe,
    title: "Pan India reach",
    description:
      "Trainers and organizations connect across cities, technologies, and industries.",
  },
  {
    number: "04",
    icon: Heart,
    title: "Fair for trainers",
    description:
      "Trainers get opportunities to showcase their expertise and grow professionally.",
  },
];

const steps = [
  {
    number: "Step 1",
    title: "Search by skill",
    description:
      "Choose a technology, select a location, and explore trainer profiles.",
  },
  {
    number: "Step 2",
    title: "Shortlist and connect",
    description:
      "Compare profiles and connect with trainers who match your requirements.",
  },
  {
    number: "Step 3",
    title: "Start training",
    description:
      "Coordinate with your selected trainer and begin your team's learning journey.",
  },
];

function About() {
  const navigate = useNavigate();

  const goTo = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

   const scrollToDirectory = () => {
  const directory = document.getElementById("trainer-directory");

  if (directory) {
    directory.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  } else {
    navigate("/trainer-register");
  }
};
  return (
    <main className="certed-about">
      <div className="about-container">

        {/* Hero */}
        <section className="about-hero">
          <div className="about-hero-content">
            <span className="about-badge">
              <span className="about-badge-dot" />
              About CertED
            </span>

            <h1>
              Building skilled
              <br />
              teams, <span>one</span>
              <br />
              <span>trainer at a time.</span>
            </h1>

            <div className="about-gold-line" />

            <p>
              CertED is a professional trainer marketplace connecting
              organizations with trainers across technology, business,
              data, and cloud. We help the right skills reach the
              right teams.
            </p>

            <div className="about-hero-actions">
              <button
                type="button"
                className="about-btn-primary"
                onClick={scrollToDirectory}
              >
                Find Trainers
              </button>

              <button
                type="button"
                className="about-btn-secondary"
                onClick={() => goTo("/contact")}
              >
                Contact us
              </button>
            </div>
          </div>

          {/* Hero visual */}
          <div className="about-hero-visual">
            <div className="about-orb about-orb-one" />
            <div className="about-orb about-orb-two" />
            <div className="about-orb about-orb-three" />

            <svg
              className="about-network"
              viewBox="0 0 340 160"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M75 100 L170 48 L270 108"
                stroke="rgba(255,255,255,0.55)"
                strokeWidth="1.5"
              />
            </svg>

            <div className="about-network-node node-one" />
            <div className="about-network-node node-two" />
            <div className="about-network-node node-three" />

            <div className="about-visual-caption">
              <span>TRAINER MARKETPLACE</span>
              <h2>
                Skills move faster when the right trainer is one
                search away.
              </h2>
            </div>
          </div>
        </section>

        {/* Statistics */}
        <section className="about-stats" aria-label="CertED statistics">
          {stats.map((stat) => (
            <div className="about-stat" key={stat.label}>
              <h2>{stat.number}</h2>
              <p>{stat.label}</p>
            </div>
          ))}
        </section>

        {/* Our story */}
        <section className="about-story">
          <div className="about-story-heading">
            <span className="about-eyebrow">OUR STORY</span>
            <h2>
              Why we <span>built CertED</span>
            </h2>
          </div>

          <div className="about-story-content">
            <p>
             At CertED, we believe finding the right trainer should be simple, transparent, and accessible. Organizations need trusted experts to build skilled teams, while trainers deserve meaningful opportunities to share their knowledge and grow professionally.

Our platform bridges this gap by connecting organizations with skilled training professionals across technologies, industries, and locations-making it easier to discover expertise, build connections, and find the right fit.

Our mission is to make quality training more accessible, empower trainers to showcase their expertise, and help organizations build a future-ready workforce through continuous learning.
            </p>
          </div>
        </section>

        {/* Principles */}
        <section className="about-principles">
          <span className="about-eyebrow">WHAT WE STAND FOR</span>

          <h2>
            Principles that <span>guide us</span>
          </h2>

          <div className="about-principles-grid">
            {principles.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  className="about-principle-card"
                  key={item.number}
                >
                  <div className="about-principle-top">
                    <div className="about-principle-icon">
                      <Icon size={19} />
                    </div>

                    <span className="about-principle-number">
                      {item.number}
                    </span>
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </article>
              );
            })}
          </div>
        </section>

      
        {/* How it works */}
        <section className="about-process">
          <span className="about-eyebrow">HOW IT WORKS</span>

          <h2>
            From search <span>to training</span>
          </h2>

          <div className="about-steps">
            {steps.map((step, index) => (
              <article className="about-step" key={step.number}>
                <div
                  className={`about-step-line ${
                    index === 0 ? "active" : ""
                  }`}
                />

                <span>{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </section>

      </div> {/* End about-container */}

      {/* Full-width Bottom CTA */}
      <section className="final-cta">
        <div className="cta-background" aria-hidden="true">
          <span style={{ top: "18%", left: "12%" }} />
          <span style={{ top: "62%", left: "78%" }} />
          <span style={{ top: "80%", left: "30%" }} />
        </div>

        <div className="cta-content">
          <h2>Ready to build your training team?</h2>

          <p>
            Find expert trainers, explore skills, and connect with
            the right training professional for your organization.
          </p>

          <div className="about-cta-actions">
            <button
              type="button"
              onClick={scrollToDirectory}
            >
              <Search size={16} />
              Find Trainers
              <ArrowRight size={16} />
            </button>

            <button
              type="button"
              onClick={() => goTo("/partner-register")}
            >
              <Building2 size={16} />
              Post Requirement
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}

export default About;

